import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useNotifications } from '../../context/NotificationContext';
import { useSubscription } from '../../hooks/useSubscription';
import DotSpinner from '../../components/shared/DotSpinner';
import { calculateAge } from '../../utils/calculateAge';
import chatService from '../../services/chat.service';
import { toast } from 'react-toastify';

// Страница лайков и симпатий: единый список карточек (лайки + взаимные + суперлайки).
// Без подписки фото заблюрены + пейвол «Хочу узнать, кто меня лайкнул».

function LikeCard({ notification, onClick }) {
  const payload = notification?.payload || {};
  const isNew = !notification?.seen;
  const isSuperlike = notification?.type === 'superlike';
  const isDeletedUser = Boolean(payload?.isDeleted);

  const userName = payload?.name || (isDeletedUser ? 'Удаленный пользователь' : 'Пользователь');
  const userAge = isDeletedUser ? '' : calculateAge(payload?.birthDay) || '';
  const userPhoto = payload?.photo;

  return (
    <div
      className="relative w-full h-[220px] overflow-hidden rounded-[24px] cursor-pointer"
      onClick={() => onClick(notification)}
    >
      {userPhoto ? (
        <img src={userPhoto} alt={userName} className="w-full h-[220px] object-cover" />
      ) : (
        <div className="w-full h-[220px] bg-[#022424] border border-[#233636] flex items-center justify-center text-white/40 text-[40px]">
          ?
        </div>
      )}

      {userPhoto && <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />}

      {isNew && (
        <div className="absolute top-[10px] right-[10px] rounded-[400px] bg-[#A1F69E] w-[58px] h-[25px] shrink-0 flex items-center justify-center text-black font-semibold text-[12px]">
          NEW
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-[12px] flex items-center gap-[6px]">
        <p className="font-raleway font-semibold text-white text-[15px] truncate">
          {userName}
          {userAge ? `, ${userAge}` : ''}
        </p>
        {isSuperlike && <img src="/icons/bg-superlike.svg" alt="Суперлайк" className="ml-auto size-[24px]" />}
      </div>
    </div>
  );
}

// Карточка лайка/суперлайка без подписки: фото под блюром, клик убирает NEW
function LockedLikeCard({ notification, onClick }) {
  const payload = notification?.payload || {};
  const isNew = !notification?.seen;

  return (
    <div className="relative w-full h-[220px] overflow-hidden rounded-[24px]" onClick={() => onClick(notification)}>
      {payload.photo ? (
        <img
          src={payload.photo}
          alt={payload.name || 'Лайкнувший пользователь'}
          className="w-full h-[220px] object-cover blur-[14px] scale-[1.08]"
        />
      ) : (
        <img src="/icons/like_locked.png" alt="locked like" className="w-full h-[220px]" />
      )}

      {payload.photo && (
        <>
          <div className="absolute inset-0 bg-black/10 backdrop-blur-[1px]" />
          <img
            src="/icons/heart-w.svg"
            alt="locked like"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 size-10 z-[5]"
          />
        </>
      )}

      {isNew && (
        <div className="absolute top-[10px] right-[10px] rounded-[400px] bg-[#A1F69E] w-[58px] h-[25px] shrink-0 flex items-center justify-center text-black font-semibold text-[12px] z-[6]">
          NEW
        </div>
      )}
    </div>
  );
}

export function Likes() {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { isActiveSubscription } = useSubscription();
  const { updateCounts } = useNotifications();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadNotifications = async () => {
      setLoading(true);
      try {
        // Единый список: лайки + взаимные симпатии + суперлайки
        // (взаимные приходят с type 'mutual' — без них страница не показывает
        // уже совпавшую симпатию, хотя карточка под неё давно заложена)
        const response = await axiosPrivate.get(config.API.NOTIFICATIONS.LIST, {
          params: { type: 'like,mutual,superlike', limit: 100 }
        });

        if (response.data?.status === 'success') {
          setNotifications(response.data.data.items || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, []);

  // Уникальные пользователи: взаимные симпатии первыми, внутри групп новейшие сверху
  const uniqueNotifications = useMemo(() => {
    const seen = new Set();
    const unique = notifications.filter((n) => {
      const id = n?.payload?._id || n._id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    return [...unique.filter((n) => n.type === 'mutual'), ...unique.filter((n) => n.type !== 'mutual')];
  }, [notifications]);

  const refreshCounts = async () => {
    try {
      const response = await axiosPrivate.get(config.API.NOTIFICATIONS.COUNTS);
      if (response.data?.status === 'success') {
        updateCounts(response.data.data);
      }
    } catch (err) {
      console.error('Error refreshing counts:', err);
    }
  };

  // Пользователь увидел карточку — отмечаем прочитанными все уведомления этого юзера
  // (у одного человека может быть несколько лайков, карточка при этом одна)
  const markUserAsRead = async (payloadUserId) => {
    if (!payloadUserId) return;

    const targets = notifications.filter((n) => !n.seen && n.payload?._id === payloadUserId);
    if (targets.length === 0) return;

    try {
      await Promise.all(
        targets.map((n) =>
          axiosPrivate.post(config.API.NOTIFICATIONS.MARK_READ(n._id)).catch((err) => console.error(err))
        )
      );
      setNotifications((prev) => prev.map((n) => (n.payload?._id === payloadUserId ? { ...n, seen: true } : n)));
      refreshCounts();
    } catch (error) {
      console.error('Error marking user notifications as read:', error);
    }
  };

  const handleCardClick = async (notification) => {
    if (!notification?._id) return;

    const payload = notification.payload || {};
    const userId = payload?._id;
    const isDeletedUser = Boolean(payload?.isDeleted);

    markUserAsRead(userId);

    if (!userId || isDeletedUser) {
      toast.error('Профиль пользователя больше недоступен');
      return;
    }

    const state = { from: config.ROUTES.LIKES };

    if (notification.type === 'mutual') {
      try {
        const res = await chatService.getChatToUserId(axiosPrivate, userId);
        navigate(config.ROUTES.CHATS.TEXT(res.data._id), { state });
      } catch (error) {
        const message = error?.response?.data?.message;
        console.error('[Likes] getChatToUserId:', error);
        toast.error(message || 'Ошибка при попытке открыть чат с пользователем');
      }
      return;
    }

    // like / superlike — профиль пользователя
    navigate(config.ROUTES.MATCHS.SUPER_LIKE_PROFILE_ID(userId, notification._id), { state });
  };

  return (
    <div className="w-full pb-[80px] flex flex-col items-center">
      <div className="mt-[16px]">
        {loading ? (
          <DotSpinner />
        ) : isActiveSubscription ? (
          uniqueNotifications.length > 0 ? (
            <div className="w-[100vw] flex justify-center">
              <div className="w-full px-4 grid grid-cols-2 gap-[16px]">
                {uniqueNotifications.map((notification) => (
                  <LikeCard key={notification._id} notification={notification} onClick={handleCardClick} />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-[400px] text-white text-center px-4 gap-7">
              <img src="/icons/heart-w.svg" className="size-[48px] opacity-60" />
              <div>
                <h4 className="text-[24px] font-raleway font-medium">Пока пусто</h4>
                <p>Здесь появятся ваши симпатии и лайки</p>
              </div>
            </div>
          )
        ) : (
          // Без подписки: взаимные симпатии открыты, лайки/суперлайки под блюром
          uniqueNotifications.length > 0 ? (
            <div className="w-[100vw] flex justify-center relative pb-[100px]">
              <div className="w-full px-4 grid grid-cols-2 gap-[16px]">
                {uniqueNotifications.map((notification) =>
                  notification.type === 'mutual' ? (
                    <LikeCard key={notification._id} notification={notification} onClick={handleCardClick} />
                  ) : (
                    <LockedLikeCard
                      key={notification._id}
                      notification={notification}
                      onClick={(n) => markUserAsRead(n?.payload?._id)}
                    />
                  )
                )}
              </div>

              {uniqueNotifications.some((n) => n.type !== 'mutual') && (
                <button
                  type="button"
                  onClick={() => navigate(config.ROUTES.PREMIUM)}
                  className="fixed bottom-[135px] left-4 right-4 h-[56px] rounded-[400px] bg-[#A1F69E] text-[#010D0D] font-raleway font-medium text-[16px] active:bg-[#7DD279] z-10"
                >
                  Хочу узнать, кто меня лайкнул
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-[400px] text-white text-center px-4 gap-7">
              <img src="/icons/heart-w.svg" className="size-[48px] opacity-60" />
              <div>
                <h4 className="text-[24px] font-raleway font-medium">Пока пусто</h4>
                <p>Здесь появятся ваши симпатии и лайки</p>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default Likes;
